/*
  Free gift promotion
  ───────────────────
  When the cart holds N or more DISTINCT products, one unit of the configured
  gift product is added automatically. When it drops below N, the gift is
  removed. Quantity is not counted: three of one product is a single line and
  does not qualify.

  Configuration comes from snippets/free-gift.liquid as window.freeGiftConfig.

  How the gift line is identified
  -------------------------------
  The gift is added with the line item property `_free_gift`. The leading
  underscore keeps it hidden from the customer in cart and checkout. That
  property is what distinguishes the promotional unit from the same product
  bought deliberately: Shopify keeps lines with different properties separate,
  so a customer who also buys the gift product gets their own line, it counts
  towards the threshold, and this script never touches it.

  Checkout safety
  ---------------
  The original build let a shopper reach checkout with a gift that was in the
  middle of being removed: the removal is two network calls, and Checkout was
  clickable throughout. Three things close that window:

    1. The cart payload published with cartUpdate is reused when it is a full
       cart, so a removal is one request instead of two.
    2. Checkout submits are intercepted. The click always runs a fresh sync to
       completion first, then re-submits, so checkout is reached with the cart
       in its settled state rather than whatever it was mid-update.
    3. While a mutation is actually in flight, body[data-free-gift-busy] makes
       the checkout and express-checkout buttons unclickable. Express buttons
       (Shop Pay, PayPal, Google Pay) are rendered by Shopify and cannot be
       intercepted, so blocking them for those few hundred milliseconds is the
       only way to keep them off a stale cart.
*/
(function () {
  const config = window.freeGiftConfig;
  if (!config || !config.enabled || !config.variantId) return;

  const PROPERTY = '_free_gift';
  const threshold = config.threshold > 0 ? config.threshold : 3;
  const BUSY_ATTR = 'data-free-gift-busy';

  let inFlight = null;
  let rerun = false;

  const isGift = (item) =>
    item.variant_id === config.variantId && item.properties && item.properties[PROPERTY] === 'true';

  /*
    Distinct PRODUCTS. Not line items, and not quantity.

    Counting lines looked equivalent to counting products until the bundle app
    proved otherwise: Shopify opens a separate line whenever the line item
    PROPERTIES differ, so one shirt bought on its own and the same shirt added
    as part of a bundle are two lines of a single product. Counting lines read
    that as two towards the threshold and handed out the gift early.

    Deduplicating on product_id is what "3 different products" actually means.
    Quantity stays ignored for the same reason - three of one thing is one thing.
  */
  const eligibleCount = (cart) => {
    const products = new Set();
    cart.items.forEach((item) => {
      if (isGift(item)) return;
      // product_id is on every /cart.js line; variant_id is a defensive fallback.
      products.add(item.product_id == null ? 'v' + item.variant_id : 'p' + item.product_id);
    });
    return products.size;
  };

  // Only trust a payload that really is a whole cart. /cart/add.js returns the
  // added line, not the cart, so it must not be mistaken for one.
  const isFullCart = (data) =>
    data && Array.isArray(data.items) && typeof data.item_count === 'number';

  const setBusy = (busy) => {
    if (busy) {
      document.body.setAttribute(BUSY_ATTR, '');
    } else {
      document.body.removeAttribute(BUSY_ATTR);
    }
    document.querySelectorAll('.cart__ctas, .cart__dynamic-checkout-buttons').forEach((el) => {
      el.setAttribute('aria-busy', busy ? 'true' : 'false');
    });
  };

  /* ------------------------------------------------------------------ */
  /* Section rendering                                                   */
  /* ------------------------------------------------------------------ */

  function sectionsToRender() {
    const sections = [];
    const add = (id, selector, target, section) => {
      if (target) sections.push({ id, selector, target, section: section || target.dataset.id || id });
    };

    add('cart-icon-bubble', '.shopify-section', document.getElementById('cart-icon-bubble'), 'cart-icon-bubble');
    add('cart-live-region-text', '.shopify-section', document.getElementById('cart-live-region-text'), 'cart-live-region-text');
    add('main-cart-items', '.js-contents', document.getElementById('main-cart-items'));
    add('main-cart-footer', '.js-contents', document.getElementById('main-cart-footer'));
    add('cart-drawer', '.js-contents', document.getElementById('CartDrawer-CartItems'), 'cart-drawer');
    add('cart-drawer-footer', '.drawer__footer', document.querySelector('.drawer__footer'), 'cart-drawer');
    return sections;
  }

  function applySections(payload) {
    if (!payload || !payload.sections) return;
    sectionsToRender().forEach((entry) => {
      const html = payload.sections[entry.section];
      if (!html) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector(entry.selector);
      if (!source) return;
      const destination = entry.target.matches(entry.selector)
        ? entry.target
        : entry.target.querySelector(entry.selector) || entry.target;
      destination.innerHTML = source.innerHTML;
    });
  }

  async function post(url, body) {
    const wanted = [...new Set(sectionsToRender().map((s) => s.section))];
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(Object.assign({}, body, { sections: wanted, sections_url: window.location.pathname }))
    });
    if (!response.ok) throw new Error(url + ' responded ' + response.status);
    return response.json();
  }

  const addGift = () =>
    post(window.routes.cart_add_url + '.js', {
      items: [{ id: config.variantId, quantity: 1, properties: { [PROPERTY]: 'true' } }]
    });

  // quantity 0 removes the line.
  const setGiftQuantity = (key, quantity) =>
    post(window.routes.cart_change_url + '.js', { id: key, quantity: quantity });

  const fetchCart = () =>
    fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  /* ------------------------------------------------------------------ */
  /* The rule                                                            */
  /* ------------------------------------------------------------------ */

  async function runOnce(known) {
    // Reuse the cart the theme just gave us rather than re-fetching it. This is
    // what turns a removal from two round trips into one.
    const cart = isFullCart(known) ? known : await fetchCart();
    const gift = cart.items.find(isGift);
    const eligible = eligibleCount(cart);

    let mutation = null;
    if (eligible >= threshold) {
      if (!gift) mutation = () => addGift();
      // The promotion is one unit only - reset anything else, however it got there.
      else if (gift.quantity !== 1) mutation = () => setGiftQuantity(gift.key, 1);
    } else if (gift) {
      mutation = () => setGiftQuantity(gift.key, 0);
    }

    if (!mutation) return false;

    setBusy(true);
    try {
      applySections(await mutation());
    } finally {
      setBusy(false);
    }
    return true;
  }

  /*
    Resolves only when the cart has settled, including any follow-up triggered
    while a run was already going. Callers can await this and know the cart is
    final - which is what makes the checkout gate reliable.
  */
  function sync(known) {
    if (inFlight) {
      rerun = true;
      return inFlight;
    }
    inFlight = (async () => {
      let payload = known;
      do {
        rerun = false;
        try {
          await runOnce(payload);
        } catch (error) {
          // A promotion must never be able to break the cart.
          console.warn('[free-gift]', error);
        }
        payload = undefined; // only the first pass may trust the passed-in cart
      } while (rerun);
    })();

    const settled = inFlight;
    settled.finally(() => {
      if (inFlight === settled) inFlight = null;
    });
    return settled;
  }

  /* ------------------------------------------------------------------ */
  /* Checkout gate                                                       */
  /* ------------------------------------------------------------------ */

  /*
    event.submitter is not available everywhere, and the checkout button lives
    outside its form (form="cart" / form="CartDrawer-Form"), so remember the last
    checkout button pressed and use it as a fallback. It is only accepted when it
    genuinely belongs to the form being submitted.
  */
  let lastCheckoutButton = null;
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest && event.target.closest('[name="checkout"]');
      if (button) lastCheckoutButton = button;
    },
    true
  );

  async function gateCheckout(form, submitter) {
    setBusy(true);
    try {
      await sync();
    } finally {
      setBusy(false);
    }

    // Sections may have been re-rendered underneath us, so look the elements up
    // again rather than reusing possibly-detached nodes.
    const liveForm = (form.id && document.getElementById(form.id)) || form;
    const liveSubmitter =
      document.querySelector('[name="checkout"][form="' + liveForm.id + '"]') ||
      liveForm.querySelector('[name="checkout"]') ||
      submitter;

    liveForm.dataset.freeGiftChecked = 'true';
    if (typeof liveForm.requestSubmit === 'function' && liveSubmitter) {
      liveForm.requestSubmit(liveSubmitter);
    } else {
      // Fallback: preserve the checkout intent that the submitter carried.
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'checkout';
      hidden.value = '';
      liveForm.appendChild(hidden);
      liveForm.submit();
    }
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!/\/cart\b/.test(form.getAttribute('action') || '')) return;

      // Only the checkout action is gated. Quantity updates and other cart
      // submits are left exactly as they were.
      let submitter = event.submitter;
      if (!submitter && lastCheckoutButton && lastCheckoutButton.form === form) {
        submitter = lastCheckoutButton;
      }
      if (!submitter || submitter.name !== 'checkout') return;

      if (form.dataset.freeGiftChecked === 'true') {
        delete form.dataset.freeGiftChecked;
        return; // our own re-submit - let it through
      }

      event.preventDefault();
      gateCheckout(form, submitter);
    },
    true
  );

  /* ------------------------------------------------------------------ */
  /* Triggers                                                            */
  /* ------------------------------------------------------------------ */

  // Every cart mutation in the theme publishes this: cart page quantity changes,
  // add to cart, quick add, and the quick order list.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => sync(event && event.cartData));
  }

  // Covers a first load, a cart restored from a previous session, and any
  // mutation made outside the theme's own pubsub.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => sync(), { once: true });
  } else {
    sync();
  }

  // Re-check when the tab regains focus, in case the cart was changed elsewhere.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) sync();
  });
})();
